import React, { useContext } from 'react';
import styles from '../../../../styles/pages/DocumentationPage/components/DocTabs/PostLead.module.css';
import { LangContext } from '../../../../context/LangContext';
import { ThemeContext } from '../../../../context/ThemeContext';

const API_BASE_URL = (process.env.REACT_APP_BASE_API_URL || '').replace(/\/$/, '');
const translations = {
    en: {
        deposit: 'Create a Deposit', withdraw: 'Create a Withdrawal',
        requestInfoTitle: 'Request Information', urlLabel: 'URL:', methodLabel: 'Method:',
        tokenLabel: 'Your Token:', loading: 'Loading...', error: 'Error', missing: 'Missing',
        pathParamsTitle: 'Path Parameters', requestParamsTitle: 'Request Parameters',
        body: 'Request Body Parameters',
        lead_id: 'ID of a lead created by your affiliate account.', token: 'Integration token.',
        requiredInteger: 'required, integer', requiredString: 'required, string',
        requiredNumber: 'required, number', optionalString: 'optional, string',
        amount: 'Positive amount in USD, with up to 8 decimal places.',
        bank: 'Bank name, up to 128 characters.',
        comment: 'Internal comment, up to 1000 characters.',
        number: 'Account or payment number, up to 255 characters. Send as a string to keep leading zeros.',
        wallet: 'Wallet address, up to 255 characters.',
        status: 'success (default), process, fail, canceled or waitaccept.',
        unique_name: 'Transaction name. Must be unique for your affiliate account and this lead across both deposits and withdrawals. No format or length validation. Can be omitted or null.',
        visibility: 'Of these optional fields, the lead sees only status. Bank, comment, number, wallet and unique_name are internal details. unique_name is returned only in affiliate transaction API responses; if omitted, it is null.',
        depositBalance: 'A successful deposit adds the amount to available balance. Other statuses create a transaction without changing the balance.',
        withdrawBalance: 'A successful withdrawal deducts the amount from available balance. Other statuses create a transaction without changing the balance. The amount must not exceed the current available balance.',
        example: 'Request Example (cURL)', exampleLabel: 'Example:',
        responses: 'Server Responses', success: 'Successful Request (201 Created)',
        notFound: 'Lead not found or does not belong to the affiliate (404 Not Found)',
        unauthorized: 'Invalid token or blocked affiliate (401 Unauthorized)',
        validation: 'Validation Error (422 Unprocessable Entity)',
        duplicateName: 'Duplicate unique_name (422 Unprocessable Entity)',
        duplicateNameDescription: 'This name is already used for this affiliate and lead. No new transaction is created.',
        insufficientFunds: 'Insufficient funds (422 Unprocessable Entity)',
        funds: 'If there are insufficient funds, the API returns 422 and no withdrawal is created:',
        retry: 'Without unique_name, each successful POST creates a new transaction. After a timeout, check whether it was created before repeating the request.',
    },
    ru: {
        deposit: 'Создание депозита', withdraw: 'Создание вывода',
        requestInfoTitle: 'Информация о запросе', urlLabel: 'URL:', methodLabel: 'Метод:',
        tokenLabel: 'Ваш токен:', loading: 'Загрузка...', error: 'Ошибка', missing: 'Отсутствует',
        pathParamsTitle: 'Параметры пути', requestParamsTitle: 'Параметры запроса',
        body: 'Параметры тела запроса',
        lead_id: 'ID лида, созданного вашим партнёрским аккаунтом.', token: 'Интеграционный токен.',
        requiredInteger: 'обязательный, integer', requiredString: 'обязательный, string',
        requiredNumber: 'обязательный, number', optionalString: 'необязательный, string',
        amount: 'Положительная сумма в USD, до 8 знаков после запятой.',
        bank: 'Название банка, до 128 символов.',
        comment: 'Внутренний комментарий, до 1000 символов.',
        number: 'Номер счёта или платежа, до 255 символов. Передавайте строкой, чтобы сохранить начальные нули.',
        wallet: 'Адрес кошелька, до 255 символов.',
        status: 'success (по умолчанию), process, fail, canceled или waitaccept.',
        unique_name: 'Название транзакции. Должно быть уникальным для вашего партнёрского аккаунта и этого лида среди депозитов и выводов. Без проверки формата и длины. Можно не передавать или указать null.',
        visibility: 'Из этих необязательных полей лид видит только status. Bank, comment, number, wallet и unique_name — внутренние данные. unique_name возвращается только в ответах партнёрского API транзакций; если не передан, возвращается null.',
        depositBalance: 'Успешный депозит увеличивает доступный баланс на указанную сумму. Другие статусы создают транзакцию без изменения баланса.',
        withdrawBalance: 'Успешный вывод уменьшает доступный баланс на указанную сумму. Другие статусы создают транзакцию без изменения баланса. Сумма не должна превышать текущий доступный баланс.',
        example: 'Пример запроса (cURL)', exampleLabel: 'Пример:',
        responses: 'Ответы сервера', success: 'Успешный запрос (201 Created)',
        notFound: 'Лид не найден или не принадлежит партнёру (404 Not Found)',
        unauthorized: 'Неверный токен или партнёр заблокирован (401 Unauthorized)',
        validation: 'Ошибка валидации (422 Unprocessable Entity)',
        duplicateName: 'Повторный unique_name (422 Unprocessable Entity)',
        duplicateNameDescription: 'Это название уже используется для данного партнёра и лида. Новая транзакция не создаётся.',
        insufficientFunds: 'Недостаточно средств (422 Unprocessable Entity)',
        funds: 'Если средств недостаточно, API возвращает 422. Транзакция вывода не создаётся:',
        retry: 'Без unique_name каждый успешный POST создаёт новую транзакцию. После таймаута сначала проверьте, была ли она создана, прежде чем повторять запрос.',
    },
};

export const CreateDeposit = ({ affiliateToken, loading, error, withdrawal = false }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const t = translations[langSelected] || translations.ru;
    const action = withdrawal ? 'withdraw' : 'deposit';
    const themed = (name) => `${styles[name]} ${theme === 'dark' ? styles['dark' + name[0].toUpperCase() + name.slice(1)] || '' : ''}`;
    const endpoint = `${API_BASE_URL}/integration/lead/leads/{lead_id}/${action}`;
    const exampleUrl = `${API_BASE_URL}/integration/lead/leads/123/${action}?token=${encodeURIComponent(affiliateToken || 'YOUR_TOKEN')}`;
    const bodyParameters = [
        { name: 'amount', meta: t.requiredNumber, example: '250.50' },
        { name: 'bank', meta: t.optionalString, example: 'Example Bank' },
        { name: 'comment', meta: t.optionalString, example: 'Payment reference' },
        { name: 'number', meta: t.optionalString, example: '0012345678' },
        { name: 'status', meta: t.optionalString, example: 'success' },
        { name: 'wallet', meta: t.optionalString, example: 'wallet-address' },
        { name: 'unique_name', meta: t.optionalString, example: 'payment-123' },
    ];
    const parameterGroups = [
        { title: t.pathParamsTitle, parameters: [{ name: 'lead_id', meta: t.requiredInteger, example: '123' }] },
        { title: t.requestParamsTitle, parameters: [{ name: 'token', meta: t.requiredString, example: 'n8902n02ed902me02ec' }] },
        { title: t.body, parameters: bodyParameters, body: true },
    ];

    return (
        <div className={themed('mainContainer')}>
            <h2 className={themed('title')}>{t[action]}</h2>
            <div className={themed('card')}>
                <h3 className={themed('cardTitle')}>{t.requestInfoTitle}</h3>
                <div className={styles.infoItem}>
                    <span className={themed('infoLabel')}>{t.urlLabel}</span>
                    <span className={themed('infoValue')}>{endpoint}</span>
                </div>
                <div className={styles.infoItem}>
                    <span className={themed('infoLabel')}>{t.methodLabel}</span>
                    <span className={themed('infoValue')}>POST</span>
                </div>
                <div className={styles.infoItem}>
                    <span className={themed('infoLabel')}>{t.tokenLabel}</span>
                    <span className={themed('token')}>{loading ? t.loading : error ? t.error : affiliateToken || t.missing}</span>
                </div>
            </div>
            {parameterGroups.map((group) => (
                <div key={group.title} className={themed('card')}>
                    <h3 className={themed('cardTitle')}>{group.title}</h3>
                    <ul className={styles.paramsList}>
                        {group.parameters.map(({ name, meta, example }) => (
                            <li key={name} className={themed('paramItem')}>
                                <div className={styles.paramName}>
                                    <span className={themed('paramKey')}>{name}</span>
                                    <span className={styles.paramMeta}>({meta})</span>
                                </div>
                                <p className={themed('paramDescription')}>
                                    {t[name]} {t.exampleLabel} <code className={themed('codeInline')}>{example}</code>
                                </p>
                            </li>
                        ))}
                    </ul>
                    {group.body && <>
                        <p className={themed('paramDescription')}>{withdrawal ? t.withdrawBalance : t.depositBalance}</p>
                        <p className={themed('paramDescription')}>{t.visibility}</p>
                    </>}
                </div>
            ))}
            <div className={themed('card')}>
                <h3 className={themed('cardTitle')}>{t.example}</h3>
                <pre className={styles.codeBlock}><code>{`curl --request POST \\
  --url '${exampleUrl}' \\
  --header 'Accept: application/json' \\
  --header 'Content-Type: application/json' \\
  --data '{"amount": 250.50, "unique_name": "payment-123"}'`}</code></pre>
                <p className={themed('paramDescription')}>{t.retry}</p>
            </div>
            <div className={themed('card')}>
                <h3 className={themed('cardTitle')}>{t.responses}</h3>
                <Response title={t.success} themed={themed} data={{
                    id: 42, lead_id: 123, amount: 250.5, assets: 'usd',
                    status: 'success', created_at: '06-09-2026 12:30:00', unique_name: 'payment-123',
                }} />
                <Response title={t.notFound} themed={themed} data={{
                    message: 'Lead not exists.',
                    errors: [{ code: 'RESOURCE_NOT_FOUND', message: 'Lead not exists.' }],
                }} />
                <Response title={t.unauthorized} themed={themed} data={{ message: 'Unauthenticated.' }} />
                <Response title={t.validation} themed={themed} data={{
                    message: 'The given data was invalid.',
                    errors: { amount: 'The amount field must be greater than 0.' },
                }} />
                <Response title={t.duplicateName} description={t.duplicateNameDescription} themed={themed} data={{
                    message: 'The given data was invalid.',
                    errors: { unique_name: 'The unique name has already been used for this lead.' },
                }} />
                {withdrawal && <Response
                    title={t.insufficientFunds}
                    description={t.funds}
                    themed={themed}
                    data={{
                        message: 'Insufficient funds: trying to withdraw 250.5, but only 100 available.',
                    }}
                />}
            </div>
        </div>
    );
};

const Response = ({ title, description, data, themed }) => (
    <div className={themed('responseGroup')}>
        <h4 className={themed('responseSubTitle')}>{title}</h4>
        {description && <p className={themed('paramDescription')}>{description}</p>}
        <pre className={styles.responseBlock}><code>{JSON.stringify(data, null, 2)}</code></pre>
    </div>
);
